import { createContext, useContext, type ReactNode } from 'react';
import { useDonnees, type EtatDonnees } from './donnees.ts';
import type { Abonnement } from './api.ts';

/**
 * Ce que l'offre du client autorise.
 *
 * ── Pourquoi un porteur, et pas un appel par écran ──────────────────────────
 * Les droits ne changent pas pendant qu'on travaille : ils sont fixés par
 * l'abonnement. Les refaire descendre à chaque écran ajouterait une requête par
 * navigation pour une réponse identique, et ferait clignoter les menus le temps
 * qu'elle revienne. Une fois par session suffit.
 *
 * ── Le serveur reste l'autorité ─────────────────────────────────────────────
 * Ce qui est masqué ici est TOUJOURS refusé côté serveur : les quotas et les
 * gardes de rôle ne dépendent pas de l'interface. Ce porteur ne protège rien,
 * il évite seulement de proposer une porte qui se refermerait — ce qui est une
 * question de respect du lecteur, pas de sécurité.
 */

export interface Droits {
  planCode: string;
  fonctionnalites: string[];
  ecritureAutorisee: boolean;
}

const Contexte = createContext<EtatDonnees<Abonnement> | null>(null);

export function FournisseurDroits({ children }: { children: ReactNode }) {
  const etat = useDonnees<Abonnement>('/billing/subscription');
  return <Contexte.Provider value={etat}>{children}</Contexte.Provider>;
}

export function useAbonnement(): EtatDonnees<Abonnement> {
  const contexte = useContext(Contexte);
  if (!contexte) throw new Error('useAbonnement doit être utilisé dans un FournisseurDroits');
  return contexte;
}

/**
 * La fonctionnalité est-elle incluse dans l'offre ?
 *
 * Répond **vrai tant que la réponse n'est pas arrivée**. C'est délibéré :
 * masquer par défaut ferait disparaître des entrées de menu pendant le
 * chargement, puis les ferait réapparaître — un scintillement qui donne
 * l'impression que l'application se répare toute seule. Et comme le serveur
 * refuse de toute façon ce qui n'est pas dû, une entrée affichée une fraction
 * de seconde de trop ne donne accès à rien.
 */
export function useFonctionnalite(nom: string): boolean {
  const abonnement = useAbonnement();
  if (!abonnement.donnees) return true;
  return abonnement.donnees.features.includes(nom);
}
